import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, CalendarDays, Clock, RefreshCw } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialList, EditorialRow, EditorialStatusPill } from "@/components/admin/EditorialList";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

// Hoisted helpers — stable references so they don't recreate on every render.
const MODIFIERS_CLASSNAMES = {
  blocked: "bg-destructive/15 text-destructive line-through hover:bg-destructive/25",
};
const startOfToday = () => new Date(new Date().setHours(0, 0, 0, 0));
const disablePast = (d) => d < startOfToday();

export default function AdminAvailability() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blocked-dates");
      setBlocks(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function remove(id) {
    try {
      await api.delete(`/admin/blocked-dates/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  // "Sync confirmed bookings" — one-shot backfill that mirrors every
  // currently-confirmed booking as an auto-block on the calendar. Safe
  // to click repeatedly (backend upserts on booking_id). Studios who
  // confirmed sessions before the feature shipped hit this once to catch
  // their calendar up.
  async function syncBookings() {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/bookings/sync-calendar");
      const msg = data.added > 0
        ? `Added ${data.added} confirmed booking${data.added === 1 ? "" : "s"} to your calendar.`
        : "Calendar is already in sync — no new bookings to add.";
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  const todayISO = format(new Date(), "yyyy-MM-dd");
  const upcoming = blocks.filter((b) => b.date >= todayISO).sort((a, b) => a.date.localeCompare(b.date) || (a.time_slot || "").localeCompare(b.time_slot || ""));
  const past = blocks.filter((b) => b.date < todayISO).sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="space-y-8" data-testid="admin-availability-page">
      <AdminPageHeader
        breadcrumb={["WORKFLOW", "AVAILABILITY"]}
        title="Block unavailable dates"
      />
      <div className="flex items-start justify-between gap-4 -mt-4 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-xl">
          Block a whole day, a range of days, or just specific time slots. Confirmed bookings automatically appear here and block their slots — customers won&apos;t be able to book during blocked times.
        </p>
        <button
          type="button"
          onClick={syncBookings}
          disabled={syncing}
          data-testid="sync-bookings-btn"
          className="inline-flex items-center gap-2 px-4 h-9 border border-[color:var(--gold-foil)]/40 hover:border-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/5 text-[10px] uppercase tracking-[0.28em] font-serif italic disabled:opacity-60 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 not-italic ${syncing ? "animate-spin" : ""}`} />
          <span className="not-italic">{syncing ? "Syncing…" : "Sync confirmed bookings"}</span>
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <Tabs defaultValue="day" className="bg-card border border-border p-4 md:p-6">
            <TabsList className="mb-6">
              <TabsTrigger value="day" data-testid="tab-day">Single day</TabsTrigger>
              <TabsTrigger value="range" data-testid="tab-range">Date range</TabsTrigger>
              <TabsTrigger value="slots" data-testid="tab-slots">Time slots</TabsTrigger>
            </TabsList>

            <TabsContent value="day">
              <SingleDayPanel blocks={blocks} reload={load} />
            </TabsContent>
            <TabsContent value="range">
              <RangePanel reload={load} />
            </TabsContent>
            <TabsContent value="slots">
              <TimeSlotPanel blocks={blocks} reload={load} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-[color:var(--gold-foil)]/25">
            <div className="px-5 py-4 border-b border-[color:var(--gold-foil)]/20 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-[color:var(--gold-base)]" />
              <h2 className="font-serif text-xl italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Upcoming blocks</h2>
            </div>
            {loading ? (
              <div className="p-6 text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Loading…</div>
            ) : upcoming.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground italic font-serif">No blocks yet.</div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto px-2" data-testid="blocked-upcoming-list">
                <EditorialList dense>
                  {upcoming.map((b) => {
                    const isBooking = b.source === "booking";
                    return (
                      <EditorialRow
                        key={b.id}
                        prefix=""
                        title={format(new Date(b.date + "T00:00:00"), "EEE, MMM d, yyyy")}
                        meta={b.reason || null}
                        status={isBooking ? (
                          <EditorialStatusPill tone="gold">
                            <Clock className="w-3 h-3 mr-1" /> {b.time_slot || "All day"} · Booked
                          </EditorialStatusPill>
                        ) : b.time_slot ? (
                          <EditorialStatusPill tone="cream">
                            <Clock className="w-3 h-3 mr-1" /> {b.time_slot}
                          </EditorialStatusPill>
                        ) : (
                          <EditorialStatusPill tone="red">Whole day</EditorialStatusPill>
                        )}
                        actions={isBooking ? (
                          <span
                            className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60 px-1"
                            title="Manage this from the Bookings page — auto-blocks are linked to the booking"
                            data-testid={`booking-block-${b.id}`}
                          >
                            Auto
                          </span>
                        ) : (
                          <button onClick={() => remove(b.id)} className="text-muted-foreground/70 hover:text-destructive transition-colors p-1" aria-label="Remove" data-testid={`unblock-${b.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      />
                    );
                  })}
                </EditorialList>
              </div>
            )}
          </div>

          {past.length > 0 && (
            <details className="border border-[color:var(--gold-foil)]/20 mt-4">
              <summary className="px-5 py-3 cursor-pointer text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors">
                Past blocks ({past.length})
              </summary>
              <div className="max-h-[300px] overflow-y-auto px-2">
                <EditorialList dense>
                  {past.map((b) => (
                    <EditorialRow
                      key={b.id}
                      prefix=""
                      title={format(new Date(b.date + "T00:00:00"), "MMM d, yyyy")}
                      meta={[b.time_slot || null, b.reason || null]}
                      actions={
                        <button onClick={() => remove(b.id)} className="text-muted-foreground/70 hover:text-destructive transition-colors p-1" aria-label="Remove">
                          <X className="w-3 h-3" />
                        </button>
                      }
                    />
                  ))}
                </EditorialList>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleDayPanel({ blocks, reload }) {
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const blockedFullDays = blocks
    .filter((b) => !b.time_slot)
    .map((b) => b.date);
  // Recreate the Date[] only when the set of blocked dates actually changes,
  // not on every keystroke in the reason field.
  const modifiers = useMemo(
    () => ({ blocked: blockedFullDays.map((d) => new Date(d + "T00:00:00")) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blockedFullDays.join("|")],
  );
  const isoFromDate = (d) => format(d, "yyyy-MM-dd");
  const pickedISO = picked ? isoFromDate(picked) : null;
  const existing = blocks.find((b) => b.date === pickedISO && !b.time_slot);

  async function toggle() {
    if (!picked) return;
    if (existing) {
      try {
        await api.delete(`/admin/blocked-dates/${existing.id}`);
        toast.success(`${pickedISO} unblocked`);
        setPicked(null);
        reload();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } else {
      try {
        await api.post("/admin/blocked-dates", { date: pickedISO, reason });
        toast.success(`${pickedISO} blocked`);
        setPicked(null);
        setReason("");
        reload();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  }

  return (
    <div>
      <Calendar
        mode="single"
        selected={picked}
        onSelect={setPicked}
        numberOfMonths={2}
        disabled={disablePast}
        modifiers={modifiers}
        modifiersClassNames={MODIFIERS_CLASSNAMES}
        className="mx-auto"
      />

      <div className="mt-6 border-t border-border pt-5 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <div className="eyebrow mb-1">Selected date</div>
          <div className="font-serif text-lg" data-testid="picked-date">
            {picked ? format(picked, "EEE, MMM d, yyyy") : "Tap a date →"}
          </div>
          {existing && <div className="text-xs text-destructive mt-1">Currently blocked: {existing.reason || "—"}</div>}
        </div>
        {picked && !existing && (
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            className="bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm sm:w-56"
            data-testid="block-reason"
          />
        )}
        <button
          onClick={toggle}
          disabled={!picked}
          className={`inline-flex items-center justify-center gap-2 px-5 h-11 text-xs uppercase tracking-[0.18em] disabled:opacity-40 ${
            existing ? "border border-border hover:border-foreground/60" : "bg-foreground text-background hover:bg-foreground/90"
          }`}
          data-testid="toggle-block-btn"
        >
          {existing ? "Unblock day" : "Block whole day"}
        </button>
      </div>

      <div className="mt-5 flex items-center gap-4 text-xs">
        <Legend swatchClass="bg-destructive/30 border border-destructive/50" label="Blocked (whole day)" />
      </div>
    </div>
  );
}

function RangePanel({ reload }) {
  const [range, setRange] = useState(undefined); // { from, to }
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!range?.from) return toast.error("Pick a start date");
    const start = format(range.from, "yyyy-MM-dd");
    const end = format(range.to || range.from, "yyyy-MM-dd");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/blocked-dates/range", { start_date: start, end_date: end, reason });
      toast.success(`Blocked ${data.added} of ${data.total_days} days`);
      setRange(undefined);
      setReason("");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Drag-select or click two dates to block a whole range (e.g., vacation).
      </p>
      <Calendar
        mode="range"
        selected={range}
        onSelect={setRange}
        numberOfMonths={2}
        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
        className="mx-auto"
      />

      <div className="mt-6 border-t border-border pt-5 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <div className="eyebrow mb-1">Selected range</div>
          <div className="font-serif text-base" data-testid="range-summary">
            {range?.from ? (
              <>
                {format(range.from, "MMM d")} → {range.to ? format(range.to, "MMM d, yyyy") : "?"}
              </>
            ) : "Tap a start and end date →"}
          </div>
        </div>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (e.g. Vacation)"
          className="bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm sm:w-56"
          data-testid="range-reason"
        />
        <button
          onClick={submit}
          disabled={!range?.from || loading}
          className="inline-flex items-center justify-center px-5 h-11 text-xs uppercase tracking-[0.18em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40"
          data-testid="block-range-btn"
        >
          {loading ? "Blocking…" : "Block range"}
        </button>
      </div>
    </div>
  );
}

function TimeSlotPanel({ blocks, reload }) {
  const [picked, setPicked] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const pickedISO = picked ? format(picked, "yyyy-MM-dd") : null;
  const slotsForDate = blocks.filter((b) => b.date === pickedISO && b.time_slot);
  const blockedSlotsSet = new Set(slotsForDate.map((s) => s.time_slot));
  const isDayFullyBlocked = blocks.some((b) => b.date === pickedISO && !b.time_slot);

  function toggleSlot(slot) {
    const next = new Set(selectedSlots);
    if (next.has(slot)) next.delete(slot);
    else next.add(slot);
    setSelectedSlots(next);
  }

  async function block() {
    if (!picked || selectedSlots.size === 0) return;
    setLoading(true);
    try {
      for (const slot of selectedSlots) {
        await api.post("/admin/blocked-dates", { date: pickedISO, time_slot: slot, reason });
      }
      toast.success(`Blocked ${selectedSlots.size} slot${selectedSlots.size > 1 ? "s" : ""}`);
      setSelectedSlots(new Set());
      setReason("");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function unblockSlot(slotBlock) {
    try {
      await api.delete(`/admin/blocked-dates/${slotBlock.id}`);
      toast.success(`Unblocked ${slotBlock.time_slot}`);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Pick a date, then choose specific time slots to block. The rest of the day stays bookable.
      </p>
      <Calendar
        mode="single"
        selected={picked}
        onSelect={(d) => { setPicked(d); setSelectedSlots(new Set()); }}
        numberOfMonths={2}
        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
        className="mx-auto"
      />

      {picked && (
        <div className="mt-6 border-t border-border pt-5 space-y-4">
          <div className="font-serif text-lg" data-testid="slot-date">
            {format(picked, "EEE, MMM d, yyyy")}
          </div>

          {isDayFullyBlocked ? (
            <div className="text-sm text-destructive">This day is already blocked as a whole day. Unblock it first if you want to manage individual slots.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="slot-grid">
                {TIME_SLOTS.map((slot) => {
                  const isBlocked = blockedSlotsSet.has(slot);
                  const isSelected = selectedSlots.has(slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => {
                        if (isBlocked) {
                          const b = slotsForDate.find((x) => x.time_slot === slot);
                          if (b) unblockSlot(b);
                        } else toggleSlot(slot);
                      }}
                      className={`h-11 text-sm border transition-colors ${
                        isBlocked
                          ? "bg-destructive/15 text-destructive border-destructive/40 line-through hover:bg-destructive/5"
                          : isSelected
                          ? "border-foreground bg-foreground text-background"
                          : "border-border hover:border-foreground/60"
                      }`}
                      data-testid={`slot-${slot}`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>

              {selectedSlots.size > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-3">
                  <div className="flex-1">
                    <div className="eyebrow mb-1">{selectedSlots.size} slot{selectedSlots.size > 1 ? "s" : ""} selected</div>
                    <div className="text-sm">{[...selectedSlots].sort().join(", ")}</div>
                  </div>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (optional)"
                    className="bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm sm:w-56"
                    data-testid="slot-reason"
                  />
                  <button
                    onClick={block}
                    disabled={loading}
                    className="inline-flex items-center justify-center px-5 h-11 text-xs uppercase tracking-[0.18em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40"
                    data-testid="block-slots-btn"
                  >
                    {loading ? "Blocking…" : "Block slots"}
                  </button>
                </div>
              )}

              <div className="flex items-center gap-4 text-xs pt-2">
                <Legend swatchClass="bg-destructive/30 border border-destructive/50" label="Already blocked (click to unblock)" />
                <Legend swatchClass="bg-foreground" label="Selected" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Legend({ swatchClass, label }) {
  return (
    <div className="inline-flex items-center gap-2 text-muted-foreground">
      <span className={`w-3 h-3 ${swatchClass}`} />
      {label}
    </div>
  );
}
